import {
  eachDayOfInterval,
  endOfDay,
  startOfDay,
  startOfToday,
  startOfTomorrow,
  subDays,
} from 'date-fns';
import { defineStore } from 'pinia';
import { Subject } from 'rxjs';

import { calculateWorkerThresholdExceededAlerts } from '@/features/capacities/capacityThresholdUtils';
import { useOrderStore } from '@/features/orders/orderStore';
import { useProjectContributorStore } from '@/features/projectContributors/store/projectContributorStore';
import { useProjectStore } from '@/features/projects/projectStore';
import { useProjectTradeStore } from '@/features/projectTrades/projectTradeStore';
import { useFeatureToggles } from '@/utils/featureToggles';

import { useScheduleState } from '../schedule/composables/useScheduleState';
import { useLocalOrderSchedulingEngineUtils } from '../schedule/engine/install';
import { AggregationResult, SummaryRowCellMouseEvent, WorkerThresholdExceededAlert } from './types';
import { useCapacitiesAccess } from './useCapacitiesAccess';
import { buildCapacitiesData, CapacitiesData, formatDateKey, getVisibleOrdersMap } from './utils';

export const useCapacityStore = defineStore('capacity-store', () => {
  const { isStartDateDuringWorkingTime } = useLocalOrderSchedulingEngineUtils();
  const contributorStore = useProjectContributorStore();
  const orderStore = useOrderStore();
  const projectStore = useProjectStore();
  const projectTradeStore = useProjectTradeStore();
  const { ENABLE_CAP_THRESHOLDS } = useFeatureToggles();
  const { scheduleIsLoaded } = useScheduleState();
  const { hasCapacitiesAccess } = useCapacitiesAccess();

  const summaryRowRendered$ = new Subject<string>();
  const summaryRowCellMouseEvents$ = new Subject<SummaryRowCellMouseEvent>();

  let dailyAggregationCache = new Map<
    string,
    {
      sumOfActivities: AggregationResult;
      sumOfWorkers: AggregationResult;
    }
  >();

  let timespanAggregationCache = new Map<
    string,
    {
      maxSumOfWorkers: AggregationResult;
      maxSumOfActivities: AggregationResult;
    }
  >();

  const clearCaches = () => {
    dailyAggregationCache = new Map();
    timespanAggregationCache = new Map();
  };

  const canShowCapacities = computed(() => hasCapacitiesAccess.value && scheduleIsLoaded.value);

  const capacitiesData = computed<CapacitiesData>(() => {
    if (!canShowCapacities.value) {
      return {
        dailyTradeSummaries: {},
        maxTradeAppearances: {},
        maxTradeWorkerCounts: {},
        maxContributorGroupWorkerCounts: {},
        timeRange: {
          start: startOfToday(),
          end: startOfTomorrow(),
        },
        tradesUsedForSummaries: new Map(),
      };
    }

    clearCaches();

    return buildCapacitiesData(
      getVisibleOrdersMap(orderStore.orders),
      projectTradeStore.trades,
      contributorStore.contributorGroups,
      isStartDateDuringWorkingTime,
    );
  });

  const dailyTradeSummaries = computed(() => capacitiesData.value.dailyTradeSummaries);
  const maxTradeAppearances = computed(() => capacitiesData.value.maxTradeAppearances);
  const maxTradeWorkerCounts = computed(() => capacitiesData.value.maxTradeWorkerCounts);
  const summaryTimeRange = computed(() => capacitiesData.value.timeRange);
  const tradesUsedForSummaries = computed(() => capacitiesData.value.tradesUsedForSummaries);

  const aggregateTradeSummariesForDate = (
    date: Date,
  ): {
    sumOfWorkers: AggregationResult;
    sumOfActivities: AggregationResult;
  } => {
    const dateKey = formatDateKey(date);

    const cachedAggregation = dailyAggregationCache.get(dateKey);
    if (cachedAggregation) return cachedAggregation;

    const sumOfActivities: AggregationResult = { key: dateKey, count: 0 };
    const sumOfWorkers: AggregationResult = { key: dateKey, count: 0 };

    const trades = dailyTradeSummaries.value[dateKey] || [];

    trades.forEach((trade) => {
      sumOfActivities.count += trade.count;
      sumOfWorkers.count += trade.workerCount;
    });

    dailyAggregationCache.set(dateKey, { sumOfActivities, sumOfWorkers });

    return { sumOfActivities, sumOfWorkers };
  };

  const aggregateTradeSummariesForTimespan = (
    startDate: Date,
    endDate: Date,
  ): {
    maxSumOfWorkers: AggregationResult;
    maxSumOfActivities: AggregationResult;
  } => {
    const intervalStart = startOfDay(startDate);
    const intervalEnd = endOfDay(subDays(endDate, 1));

    const timespanAggregationCacheKey = `${formatDateKey(intervalStart)}-${formatDateKey(intervalEnd)}`;
    const cachedAggregationResult = timespanAggregationCache.get(timespanAggregationCacheKey);
    if (cachedAggregationResult) return cachedAggregationResult;

    const daysInRange = eachDayOfInterval({
      start: intervalStart,
      end: intervalEnd,
    });

    const maxSumOfActivities: AggregationResult = { key: null, count: 0 };
    const maxSumOfWorkers: AggregationResult = { key: null, count: 0 };

    daysInRange.forEach((day) => {
      const dateKey = formatDateKey(day);
      const tradeSummaries = dailyTradeSummaries.value[dateKey] || [];
      let totalActivityCount = 0;
      let totalWorkerCount = 0;

      tradeSummaries.forEach((tradeSummary) => {
        totalActivityCount += tradeSummary.count;
        totalWorkerCount += tradeSummary.workerCount;
      });

      if (totalActivityCount > maxSumOfActivities.count) {
        maxSumOfActivities.key = dateKey;
        maxSumOfActivities.count = totalActivityCount;
      }
      if (totalWorkerCount > maxSumOfWorkers.count) {
        maxSumOfWorkers.key = dateKey;
        maxSumOfWorkers.count = totalWorkerCount;
      }
    });

    const result = {
      maxSumOfWorkers,
      maxSumOfActivities,
    };

    timespanAggregationCache.set(timespanAggregationCacheKey, result);

    return result;
  };

  /**
   * Maximum values across all days in the project
   */
  const capacityPeaks = computed(() => {
    let activities = 0;
    let workers = 0;

    for (const tradeSummaries of Object.values(dailyTradeSummaries.value)) {
      let dayTotalWorkers = 0;
      let dayTotalActivities = 0;

      tradeSummaries.forEach((tradeSummary) => {
        dayTotalWorkers += tradeSummary.workerCount;
        dayTotalActivities += tradeSummary.count;
      });

      if (dayTotalWorkers > workers) workers = dayTotalWorkers;
      if (dayTotalActivities > activities) activities = dayTotalActivities;
    }

    return { workers, activities };
  });

  const contributorGroupWorkerPeaks = computed(() => {
    const peaks: Record<string, number> = {};

    const allContributorGroups = [
      ...contributorStore.internalContributorGroups,
      ...contributorStore.externalContributorGroups,
    ];

    allContributorGroups.forEach((group) => {
      peaks[group.id] = 0;
    });

    for (const summaries of Object.values(capacitiesData.value.maxContributorGroupWorkerCounts)) {
      for (const [contributorGroupId, workerCount] of Object.entries(summaries)) {
        peaks[contributorGroupId] = Math.max(peaks[contributorGroupId], workerCount);
      }
    }

    return peaks;
  });

  const workerThresholdExceededAlertList = computed<WorkerThresholdExceededAlert[]>(() => {
    if (!projectStore.currentProject || !ENABLE_CAP_THRESHOLDS.value) return [];
    const { isStartDateDuringWorkingTime } = useLocalOrderSchedulingEngineUtils();

    return calculateWorkerThresholdExceededAlerts(
      orderStore.orders,
      contributorStore.contributorGroups,
      projectStore.currentProject,
      isStartDateDuringWorkingTime,
    );
  });
  const workerThresholdExceededAlerts = computed<Map<string, WorkerThresholdExceededAlert>>(() => {
    return new Map(
      workerThresholdExceededAlertList.value.map((capacityCollision) => [
        capacityCollision.id,
        capacityCollision,
      ]),
    );
  });

  return {
    capacityPeaks,
    contributorGroupWorkerPeaks,
    dailyTradeSummaries,
    maxTradeAppearances,
    maxTradeWorkerCounts,
    summaryRowCellMouseEvents$,
    summaryRowRendered$,
    summaryTimeRange,
    tradesUsedForSummaries,
    workerThresholdExceededAlertList,
    workerThresholdExceededAlerts,
    aggregateTradeSummariesForDate,
    aggregateTradeSummariesForTimespan,
  };
});

export type CapacityStore = ReturnType<typeof useCapacityStore>;
